import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { CURRENT_FY, NoPayrollAccess, fyOf, usePayrollAccess } from './shared';
import { Overview } from './Overview';
import { SiteView } from './SiteView';
import { MonthView } from './MonthView';
import { SiteMonthView } from './SiteMonthView';
import { PersonView } from './PersonView';

/**
 * Payroll — salary sheets per site and wage month. Read-only prototype.
 * Brief: smart-bin-backend/docs/superpowers/specs/2026-10-18-payroll-ui-brief.md (screens 1-3).
 *
 * Five levels, all in the URL so links and the Back button work:
 *   Overview (all sites x months)  ?fy=
 *   Site (one site, every month)   ?fy=&site=
 *   Month (every site, one month)  ?fy=&month=
 *   Site + month (people)          ?fy=&site=&month=
 *   Person (one person, months)    ...&person=
 * Overview, Site and Month all read /payroll/matrix; only Site+month and Person have their own calls.
 *
 * Rules kept from the brief: "—" is not 0, the summary row is never added, wage month is the axis,
 * "Not checked yet" is grey (unknown must never look wrong). Bank and statutory checks are not built.
 */

/* ── page ───────────────────────────────────────────────────────────────────── */

export const PayrollPage: React.FC = () => {
	const [params, setParams] = useSearchParams();
	const month = params.get('month');
	const fy = params.get('fy') || (month ? fyOf(month) : CURRENT_FY);
	const site = params.get('site');
	const person = params.get('person');
	const allowed = usePayrollAccess();
	if (!allowed) return <NoPayrollAccess />;

	let view: React.ReactNode;
	if (person) view = <PersonView fy={fy} site={site} month={month} name={person} />;
	else if (site && month) view = <SiteMonthView fy={fy} site={site} month={month} />;
	else if (site) view = <SiteView fy={fy} site={site} />;
	else if (month) view = <MonthView fy={fy} month={month} />;
	else view = <Overview fy={fy} setFy={f => setParams({ fy: f })} />;

	return <div className='px-6 py-6 max-w-[1500px] mx-auto bg-gray-50/40 min-h-full'>{view}</div>;
};

export default PayrollPage;
